import { useMemo } from 'react';
import {
  anyInternalAssetToRpcAsset,
  getInternalAsset,
  type AnyChainflipAsset,
} from '@chainflip/utils/chainflip';
import { useQuery } from '@tanstack/react-query';
import { isChainflipTokenOrChain, type Token } from '@/shared/assets/tokens';
import { integrationManager } from '@/shared/integrations';
import { CHAINFLIP_BOOST_LIQUIDITY } from '@/shared/queryKeys';

export const useBoostPoolsDepth = ({
  token,
  tier,
}: {
  token?: Token;
  tier?: number;
} = {}) => {
  const filter = useMemo(() => {
    if (!isChainflipTokenOrChain(token)) return {};

    return {
      ...(token && {
        chainAsset: {
          asset: anyInternalAssetToRpcAsset[token.chainflipId].asset,
          chain: token.chain.chainflipId,
        },
      }),
      ...(tier && {
        tier,
      }), // TODO(2.2): remove tier filtering, we only have 5 bps boosts moving forward
    };
  }, [token, tier]);

  const { data: poolDepths, isLoading } = useQuery({
    queryKey: [CHAINFLIP_BOOST_LIQUIDITY],
    queryFn: async () =>
      integrationManager.getIntegration('chainflip').sdk.getBoostLiquidity(filter),
    refetchInterval: 10_000,
  });

  const maxBoostSwapAmounts = useMemo(
    () =>
      poolDepths?.reduce(
        (amounts, poolDepth) => {
          const internalAsset = getInternalAsset(poolDepth);

          return {
            ...amounts,
            ...(internalAsset && {
              [internalAsset]: (amounts[internalAsset] ?? BigInt(0)) + poolDepth.availableAmount,
            }),
          };
        },
        {} as Record<AnyChainflipAsset, bigint>,
      ),
    [poolDepths],
  );

  return {
    poolDepths,
    maxBoostSwapAmounts,
    isLoading,
  };
};
